import { useMemo } from 'react';

import { Box } from 'approot/shared/design-system/box/box.style';
import { CONTAINER_WIDTH } from '../suggested-answer.constants';
import { SUGGESTED_ANSWER_VARY } from 'global/constants';
import { ComponentInteractiveResponseSequence } from '../../component.constants';
import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import ActivityBuilderComponentPreview from 'approot/activity-builder/page/activity-builder-component-preview';
import { COMPONENT_SELECTOR } from '../../shared/logic/get-component-selector-id';
import { useFindComponentDimensions } from '../../component.hooks';
import { isRichtextValueEmpty } from '../../shared/logic/get-richtext-value';
import ActivityPresentModal from 'approot/activity-builder/present/activity-present-modal';
import SuggestedAnswerTitle from '../suggested-answer-title';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { getSubActivityLabel } from '../suggested-answer.utils';
import { useGetRichtextAnswer } from '../suggested-answer.hooks';
import SuggestedAnswerPreview from './suggested-answer-preview';
import InteractiveResponseSequenceStatic from '../../interactive-response/sequence/play/interactive-response-sequence-static';
import { SuggestedAnswerPreviewContainer } from '../suggested-answer.style';
import InteractiveResponseSequenceSuggestedAnswer from '../../interactive-response/sequence/play/interactive-response-sequence-suggested-answer';
import SuggestedAnswerViewBadges from './suggested-answer-view-badges';
import { RESPONSE_FREE } from '../../interactive-response/sequence/settings/interactive-response.constants';
import { PStyled } from 'approot/shared/styles/typography.style';
import { hasNoRichTextData } from './suggested-answer-view.utils';
import SuggestedAnswerExampleRichtext from './suggested-answer-example-richtext';

type Props = {
  component: ComponentInteractiveResponseSequence;
  isUnitQuiz: boolean;
};

const SuggestedAnswerSequenceView = ({ component, isUnitQuiz }: Props) => {
  const { id, subActivityProperties, suggestedAnswerProperties, properties } =
    component;

  const answerType = suggestedAnswerProperties?.suggestedAnswerType;

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const { width, height } = useFindComponentDimensions(id, COMPONENT_SELECTOR);

  const isFreeResponse =
    properties.interactiveResponse.answerType === RESPONSE_FREE;

  const hasNoData = useMemo(
    () => isFreeResponse && hasNoRichTextData(suggestedAnswerProperties),
    [isFreeResponse, suggestedAnswerProperties]
  );

  const answer = useGetRichtextAnswer({
    componentId: id,
    suggestedAnswerProperties,
  });

  const score = useMemo(() => {
    if (isFreeResponse) {
      return suggestedAnswerProperties?.suggestedAnswersScore;
    }
    return 1;
  }, [isFreeResponse, suggestedAnswerProperties]);

  return hasNoData && !isUnitQuiz ? null : (
    <Box>
      <SuggestedAnswerTitle
        isSmall
        label={subActivityProperties?.subActivityLabel}
        instructions={subActivityProperties?.instructions}
      />

      <SuggestedAnswerViewBadges
        isUnitQuiz={isUnitQuiz}
        isExampleAnswer={isFreeResponse && !isRichtextValueEmpty(answer)}
        suggestedAnswerScore={score}
      />

      {isVisible && (
        <ActivityPresentModal
          isVisible
          title="Viewing Answers"
          onClose={closeModal}
          size={1000}
        >
          <SuggestedAnswerTitle
            isLarge
            label={subActivityProperties?.subActivityLabel}
            instructions={subActivityProperties?.instructions}
          />
          <InteractiveResponseSequenceSuggestedAnswer
            {...component}
            subActivityProperties={getSubActivityLabel(subActivityProperties)}
          />
        </ActivityPresentModal>
      )}

      {!isFreeResponse && (
        <SettingsGroup marginBottom={8}>
          <SuggestedAnswerPreviewContainer>
            <ActivityBuilderComponentPreview
              container={{
                width: CONTAINER_WIDTH,
              }}
              content={{
                width,
                height,
              }}
            >
              <InteractiveResponseSequenceStatic {...component} />
            </ActivityBuilderComponentPreview>
          </SuggestedAnswerPreviewContainer>

          <SuggestedAnswerPreview
            ariaLabel="View matching and sequencing response answers"
            onClick={openModal}
          />
        </SettingsGroup>
      )}

      {answerType === SUGGESTED_ANSWER_VARY ? (
        <PStyled>Answers may vary</PStyled>
      ) : (
        <SuggestedAnswerExampleRichtext
          id={id}
          suggestedAnswerProperties={suggestedAnswerProperties}
        />
      )}
    </Box>
  );
};

export default SuggestedAnswerSequenceView;
